import React, { useEffect, useState } from 'react';
import { FaMinus, FaPlus, FaCheckCircle } from 'react-icons/fa';

// Pas d'ajustement du prix (FCFA), aligné sur QuickDelivery.
const PRICE_STEP = 100;

// Page publique "augmenter mon offre" ouverte depuis le lien WhatsApp :
// /modifier-prix?id=<parcelId>&access=<trackingID>. Le client ne peut que RELEVER
// son prix, tant que la course n'a pas été acceptée par un livreur.
export default function ModifyPricePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null); // { tariff, platformTariff, editable, state, trackingID, senderName }
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const apiBase = process.env.REACT_APP_API_ENDPOINT;

  useEffect(() => {
    if (!token) {
      setError('Lien invalide.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${apiBase}/v1/public/order/price?token=${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error('Lien invalide ou expiré.');
        const data = await res.json();
        setInfo(data);
        setPrice(data?.tariff != null ? String(data.tariff) : '');
      } catch (e) {
        setError(e.message || 'Erreur de chargement.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, apiBase]);

  const floor = info?.tariff ?? 0; // augmenter uniquement : plancher = prix actuel
  const bump = (delta) => {
    const current = Number(price) || floor;
    setPrice(String(Math.max(floor, Math.round(current + delta))));
  };
  const clampOnBlur = () => {
    const n = Number(price);
    if (!Number.isFinite(n) || n < floor) setPrice(String(floor));
    else setPrice(String(Math.round(n)));
  };

  const submit = async () => {
    setError('');
    const n = Number(price);
    if (!Number.isFinite(n) || n <= floor) {
      setError(`Votre nouvelle offre doit être supérieure à ${floor} FCFA.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/v1/public/order/price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, tariff: n }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.message || '';
        if (msg.startsWith('PRICE_LOCKED')) throw new Error("Cette course a déjà été acceptée : le prix n'est plus modifiable.");
        if (msg.startsWith('PRICE_TOO_LOW')) throw new Error(`Votre offre doit dépasser ${floor} FCFA.`);
        throw new Error('Modification impossible. Réessayez.');
      }
      setInfo(data);
      setDone(true);
    } catch (e) {
      setError(e.message || 'Erreur.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* En-tête marque */}
        <div className="text-center mb-6">
          <span className="text-3xl font-extrabold">
            <span style={{ color: '#113039' }}>Oui</span><span style={{ color: '#f89133' }}>Goo</span>
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Chargement…</p>
          ) : error && !info ? (
            <p className="text-center text-red-600 py-10">{error}</p>
          ) : done ? (
            <div className="text-center py-6">
              <FaCheckCircle className="mx-auto w-12 h-12 text-green-500 mb-3" />
              <h2 className="text-lg font-bold text-gray-900">Offre enregistrée</h2>
              <p className="mt-2 text-sm text-gray-600">
                Votre nouvelle offre de <span className="font-semibold">{info?.tariff} FCFA</span> a bien été prise en compte.
                Un livreur pourra désormais accepter votre course plus rapidement.
              </p>
            </div>
          ) : info && !info.editable ? (
            <div className="text-center py-6">
              <h2 className="text-lg font-bold text-gray-900">Course déjà acceptée</h2>
              <p className="mt-2 text-sm text-gray-600">Un livreur a déjà pris en charge cette course. Le prix n'est plus modifiable.</p>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-gray-900">Augmentez votre offre</h1>
              <p className="mt-1 text-sm text-gray-500">
                Proposez un meilleur prix pour la livraison {info?.trackingID} afin de trouver rapidement un livreur.
              </p>

              <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 text-center mb-2">Votre offre</p>
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    aria-label="Baisser"
                    onClick={() => bump(-PRICE_STEP)}
                    className="flex items-center justify-center w-11 h-11 rounded-full bg-orange-100 text-orange-600 hover:bg-orange-200 active:scale-95 transition disabled:opacity-40"
                    disabled={Number(price) <= floor}
                  >
                    <FaMinus className="w-4 h-4" />
                  </button>
                  <div className="flex-1 text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      <input
                        type="number"
                        inputMode="numeric"
                        value={price}
                        min={floor}
                        onChange={(e) => setPrice(e.target.value)}
                        onBlur={clampOnBlur}
                        className="w-28 text-3xl font-extrabold text-gray-900 bg-transparent text-center focus:outline-none"
                      />
                      <span className="text-sm font-semibold text-gray-500">FCFA</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Prix actuel : {info?.tariff} FCFA</p>
                  </div>
                  <button
                    type="button"
                    aria-label="Augmenter"
                    onClick={() => bump(PRICE_STEP)}
                    className="flex items-center justify-center w-11 h-11 rounded-full bg-orange-100 text-orange-600 hover:bg-orange-200 active:scale-95 transition"
                  >
                    <FaPlus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

              <button
                type="button"
                onClick={submit}
                disabled={submitting || Number(price) <= floor}
                className="mt-5 w-full rounded-xl py-3.5 text-white font-semibold transition disabled:opacity-50"
                style={{ backgroundColor: '#f89133' }}
              >
                {submitting ? 'Envoi…' : 'Valider ma nouvelle offre'}
              </button>
              <p className="mt-3 text-center text-[11px] text-gray-400">Vous ne pouvez qu'augmenter votre offre.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
